import { useEffect } from 'react';
import { create } from 'zustand';
import { Badge } from './Badge';
import type { BadgeTone } from './Badge';

export type ToastTone = 'info' | 'ok' | 'caution' | 'warning' | 'critical';

export interface ToastItem {
  id: string;
  tone: ToastTone;
  title: string;
  description?: string;
  /** 0 表示不自动关闭 */
  timeoutMs: number;
}

interface ToastState {
  items: ToastItem[];
  push: (toast: Omit<ToastItem, 'id' | 'timeoutMs'> & { timeoutMs?: number }) => string;
  dismiss: (id: string) => void;
  clear: () => void;
}

let seq = 0;

const useToastStore = create<ToastState>((set) => ({
  items: [],
  push: (input) => {
    seq += 1;
    const id = `toast_${seq}`;
    const item: ToastItem = {
      id,
      tone: input.tone,
      title: input.title,
      description: input.description,
      timeoutMs: input.timeoutMs ?? (input.tone === 'critical' || input.tone === 'warning' ? 8000 : 4000),
    };
    set((state) => ({ items: [...state.items.slice(-4), item] }));
    return id;
  },
  dismiss: (id) => set((state) => ({ items: state.items.filter((item) => item.id !== id) })),
  clear: () => set({ items: [] }),
}));

/**
 * 全局提示条。与浮层系统同源（都在 `#overlay-root` 之外独立定位），
 * 但**不会**渲染蒙版，因此在单机管理页也可以安全使用。
 */
export const toast = {
  show: (input: Omit<ToastItem, 'id' | 'timeoutMs'> & { timeoutMs?: number }) =>
    useToastStore.getState().push(input),
  info: (title: string, description?: string) =>
    useToastStore.getState().push({ tone: 'info', title, description }),
  ok: (title: string, description?: string) =>
    useToastStore.getState().push({ tone: 'ok', title, description }),
  caution: (title: string, description?: string) =>
    useToastStore.getState().push({ tone: 'caution', title, description }),
  warning: (title: string, description?: string) =>
    useToastStore.getState().push({ tone: 'warning', title, description }),
  critical: (title: string, description?: string) =>
    useToastStore.getState().push({ tone: 'critical', title, description }),
  dismiss: (id: string) => useToastStore.getState().dismiss(id),
  clear: () => useToastStore.getState().clear(),
};

const TONES: Record<ToastTone, BadgeTone> = {
  info: 'info',
  ok: 'ok',
  caution: 'caution',
  warning: 'warning',
  critical: 'critical',
};

const LABELS: Record<ToastTone, string> = {
  info: '提示',
  ok: '成功',
  caution: '注意',
  warning: '警告',
  critical: '严重',
};

function ToastCard({ item }: { item: ToastItem }) {
  const dismiss = useToastStore((state) => state.dismiss);

  useEffect(() => {
    if (item.timeoutMs <= 0) return;
    const timer = window.setTimeout(() => dismiss(item.id), item.timeoutMs);
    return () => window.clearTimeout(timer);
  }, [dismiss, item.id, item.timeoutMs]);

  return (
    <div
      role="status"
      className={[
        'pointer-events-none flex w-80 max-w-[calc(100vw-2rem)] flex-col gap-1.5 rounded-lg border bg-surface-1 px-3.5 py-3',
        item.tone === 'critical'
          ? 'border-critical/70'
          : item.tone === 'warning'
            ? 'border-warning/60'
            : 'border-line-strong',
      ].join(' ')}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <Badge tone={TONES[item.tone]} solid={item.tone === 'critical' || item.tone === 'warning'}>
            {LABELS[item.tone]}
          </Badge>
          <p className="text-sm font-medium text-fg">{item.title}</p>
        </div>
        <button
          type="button"
          aria-label="关闭提示"
          onClick={() => dismiss(item.id)}
          className="pointer-events-auto -mt-0.5 -mr-1 rounded px-1 text-xs text-fg-subtle hover:text-fg"
        >
          ✕
        </button>
      </div>
      {item.description ? <p className="text-xs leading-relaxed text-fg-muted">{item.description}</p> : null}
    </div>
  );
}

export function ToastViewport() {
  const items = useToastStore((state) => state.items);
  if (items.length === 0) return null;
  return (
    // z 层级阶梯：页面内容 < 通知 1150 < 按钮确认气泡 1200 < 功能性弹窗 1300
    // 通知卡片本身不接收指针事件（只有 ✕ 按钮接收），避免遮挡下方按钮点击
    <div className="pointer-events-none fixed right-4 bottom-4 z-[1150] flex flex-col items-end gap-2">
      {items.map((item) => (
        <ToastCard key={item.id} item={item} />
      ))}
    </div>
  );
}
